import React, { useCallback, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Dropdown } from 'antd';
import { useIntl } from 'react-intl';

import {
  copyTaskLink,
  deleteTaskAction,
  duplicateTaskAction,
  markTaskAsCompleteIncomplete,
  copyAllTaskPropertiesToTmp,
  archiveTaskAction,
  openTaskAction,
} from '../../../../../redux/tasks/actions';
import { isTicketOpen } from '../../../../../actions';

import MarkAsLabel from './MarkAsLabel';
import { compact, isEmpty } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { registerContextMenuCloser } from '../../../../../js/functions';
import { toggleDuplicateTaskModal } from '../../../../../actions/modalAction';
import { isWorkfloSuperAdminSelector } from '../../../../../redux/user/selectors';
import { isCurrentWorkspaceOwnerOrAdmin } from '../../../../../redux/general/selectors';
import { getTaskProjectIdListSelector, isTaskCreator } from '../../../../../redux/tasks/taskEntities/selectors';
import { hasDeleteTaskRightsBasedOnRoleSelector, isCurrentUserProjectOwnerAdminMember } from '../../../../../redux/dashboards/selectors';

let currentlyOpenKey = null;

const TaskCardWithDropdown = ({
  children,
  setIsEdit,
  taskId,
  titleIdentifier,
  isSubtask = false,
  hasArchiveTaskRightsBasedOnRole,
  hasDeleteTaskRightsBasedOnRole,
  isTaskCreator,
  isWorkspaceOwnerAdmin,
  isWorkfloSuperAdmin
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const [isOpen, setIsOpen] = useState(false);

  const handleCopyLink = useCallback(() => dispatch(copyTaskLink(taskId, titleIdentifier, intl)), [taskId]);

  const hasTaskArchiveRights = useMemo(
    () => hasArchiveTaskRightsBasedOnRole || isTaskCreator, // || isWorkspaceOwnerAdmin || isWorkfloSuperAdmin,
    [
      hasArchiveTaskRightsBasedOnRole,
      isTaskCreator,
      // isWorkfloSuperAdmin,
      // isWorkspaceOwnerAdmin,
    ],
  );

  const hasTaskDeleteRights = useMemo(
    () => hasDeleteTaskRightsBasedOnRole || isTaskCreator, // || isWorkspaceOwnerAdmin || isWorkfloSuperAdmin,
    [
      hasDeleteTaskRightsBasedOnRole,
      isTaskCreator,
      // isWorkfloSuperAdmin,
      // isWorkspaceOwnerAdmin,
    ],
  );

  const handleEditTask = useCallback(() => {
    dispatch(copyAllTaskPropertiesToTmp(taskId));
    setIsEdit(taskId);
  }, [taskId]);

  const handleDuplicateTask = useCallback(() => {
    dispatch(duplicateTaskAction(taskId));
  }, [taskId]);

  const handleDeleteTask = useCallback(() => {
    dispatch(deleteTaskAction(taskId, () => { }));
    !isSubtask && dispatch(isTicketOpen(false));
  }, [isSubtask, taskId]);

  const handleArchiveTask = useCallback(() => {
    const { 1: selectedProjectId } = window.location.pathname.split("/");
    dispatch(archiveTaskAction(taskId, selectedProjectId, () => { }));
    !isSubtask && dispatch(isTicketOpen(false));
  }, [isSubtask, taskId]);

  const handleMarkTaskAsCompleteIncomplete = useCallback(() => {
    dispatch(markTaskAsCompleteIncomplete(taskId, intl));
  }, [taskId]);

  const handleOpen = useCallback(() => {
    dispatch(openTaskAction(true, titleIdentifier, taskId));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [titleIdentifier],
);

  const dropdownMenuItems = useMemo(
    () => ({
      items: compact([
        {
          key: `dropdownMenu-${taskId}-1`,
          label: 'Copy Link',
          onClick: handleCopyLink,
        },
        setIsEdit && {
          key: `dropdownMenu-${taskId}-2`,
          label: 'Edit',
          onClick: handleEditTask,
        },
        {
          key: `dropdownMenu-${taskId}-3`,
          label: <MarkAsLabel taskId={taskId} />,
          onClick: handleMarkTaskAsCompleteIncomplete,
        },
        {
          key: `dropdownMenu-${taskId}-4`,
          label: 'Duplicate Task',
          onClick: () => { dispatch(copyAllTaskPropertiesToTmp(taskId)); dispatch(toggleDuplicateTaskModal(true)) },
        },
        (hasTaskArchiveRights && {
          key: `dropdownMenu-${taskId}-5`,
          label: <div className="text-purple-600 font-14">Archive Task</div>,
          onClick: handleArchiveTask,
        }),
        (hasTaskDeleteRights && {
          key: `dropdownMenu-${taskId}-6`,
          label: <div className="text-red-600 font-14">Delete</div>,
          onClick: handleDeleteTask,
        }),
      ]),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasTaskArchiveRights,
      hasTaskDeleteRights,
      handleArchiveTask,
      handleCopyLink,
      handleDeleteTask,
      handleDuplicateTask,
      handleEditTask,
      handleMarkTaskAsCompleteIncomplete,
      setIsEdit,
      taskId
    ],
  );

  const dropdownSubtaskMenuItems = useMemo(
    () => ({
      items: compact([
        {
          key: `dropdownMenu-${taskId}-1`,
          label: 'Copy Link',
          onClick: handleCopyLink,
        },
        {
          key: `dropdownMenu-${taskId}-2`,
          label: 'Open task details',
          onClick: handleOpen,
        },
        setIsEdit && {
          key: `dropdownMenu-${taskId}-3`,
          label: 'Edit',
          onClick: handleEditTask,
        },
        {
          key: `dropdownMenu-${taskId}-4`,
          label: 'Duplicate Task',
          onClick: () => {
            dispatch(copyAllTaskPropertiesToTmp(taskId));
            dispatch(toggleDuplicateTaskModal(true));
          },
        },
        hasTaskDeleteRights && {
          key: `dropdownMenu-${taskId}-5`,
          label: <div className="text-red-600 font-14">Delete</div>,
          onClick: handleDeleteTask,
        },
      ]),
    }),
    [
      hasTaskDeleteRights,
      handleCopyLink,
      handleDeleteTask,
      handleEditTask,
      setIsEdit,
      taskId,
    ],
  );

  return (
    <Dropdown
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          registerContextMenuCloser(() => setIsOpen(false));

          if (currentlyOpenKey && currentlyOpenKey !== taskId) {
            currentlyOpenKey.setIsOpen(false);
          }
          currentlyOpenKey = { id: taskId, setIsOpen };
          setIsOpen(true);
        } else {
          if (currentlyOpenKey?.id === taskId) currentlyOpenKey = null;
          setIsOpen(false);
        }
      }}
      menu={isSubtask ? dropdownSubtaskMenuItems : dropdownMenuItems} trigger={['contextMenu']}>
      <div onContextMenu={(e) => { e.stopPropagation() }} className='w-full'>
        {children}
      </div>
    </Dropdown>
  );
};

const mapStateToProp = (state, ownProps) => {
  const taskProjectIdList = getTaskProjectIdListSelector(state, {
    taskId: ownProps?.taskId,
  });

  return {
    hasDeleteTaskRightsBasedOnRole: isEmpty(taskProjectIdList)
      ? false
      : taskProjectIdList?.some((projectId) =>
          hasDeleteTaskRightsBasedOnRoleSelector(state, { projectId }),
        ),
    hasArchiveTaskRightsBasedOnRole: isEmpty(taskProjectIdList)
      ? false
      : taskProjectIdList?.some((projectId) =>
          isCurrentUserProjectOwnerAdminMember(state, { projectId }),
        ),
    isTaskCreator: isTaskCreator(state, { taskId: ownProps?.taskId }),
    isWorkspaceOwnerAdmin: isCurrentWorkspaceOwnerOrAdmin(state),
    isWorkfloSuperAdmin: isWorkfloSuperAdminSelector(state),
  };
};

export default connect(mapStateToProp)(TaskCardWithDropdown);